// Cuadro de la copa: 32 equipos, dieciseisavos a final.
import { simularPartido } from './matchSim';

export const RONDAS = ['Dieciseisavos', 'Octavos', 'Cuartos', 'Semifinal', 'Final'];
export const EQUIPOS = 32;

/**
 * Tope de valor del rival que te puede tocar en cada ronda. La copa arranca accesible y se
 * endurece: de cuartos en adelante no hay limite y te puede tocar cualquiera.
 */
export const TOPES = [500, 750, Infinity, Infinity, Infinity];

/**
 * Índices del cuadro de los que puede salir tu rival en la ronda `r`.
 *
 * En un cuadro, el rival de la ronda r sale del bloque "hermano" del tuyo: comparte con vos el
 * bloque de la ronda siguiente pero no el de esta. Con posiciones de 0 a 31 eso se calcula con
 * corrimientos de bits, sin tener que recorrer el cuadro.
 */
function bloqueRival(miPos, r) {
    const idx = [];
    for (let j = 0; j < EQUIPOS; j++) {
        if ((j >> (r + 1)) === (miPos >> (r + 1)) && (j >> r) !== (miPos >> r)) idx.push(j);
    }
    return idx;
}

/**
 * Arma el cuadro completo: tu equipo entra en una posición al azar entre los 31 rivales.
 *
 * El sorteo sigue siendo al azar, pero SEMBRADO: los equipos que pueden llegar a cruzarte en las
 * primeras rondas se eligen respetando el tope de TOPES, así los dieciseisavos no te ponen de
 * entrada contra un rival de 1200M. Del resto del cuadro no se controla nada.
 *
 * La gracia de un cuadro es no saber contra quién jugás la fase siguiente hasta que esos dos se
 * enfrenten, así que los otros cruces también se juegan — se simulan de una y sus ganadores van
 * avanzando por su rama.
 */
export function armarCuadro(miEquipo, rivales) {
    const miPos = Math.floor(Math.random() * EQUIPOS);
    const libres = [...rivales].sort(() => Math.random() - 0.5);
    const posiciones = new Array(EQUIPOS).fill(null);
    posiciones[miPos] = { tipo: 'propio', ...miEquipo };

    // Primero las posiciones con tope, de la mas exigente a la mas laxa: si se llenaran al final
    // ya no quedarian rivales baratos para cumplirlo.
    TOPES.forEach((tope, r) => {
        if (!isFinite(tope)) return;
        for (const j of bloqueRival(miPos, r)) {
            if (posiciones[j]) continue;
            let i = libres.findIndex(x => x.fuerza <= tope);
            // Si no quedan rivales bajo el tope, se usa el mas barato disponible: es preferible
            // pasarse un poco a dejar el cuadro incompleto.
            if (i < 0) i = libres.reduce((mejor, x, k, arr) => x.fuerza < arr[mejor].fuerza ? k : mejor, 0);
            posiciones[j] = { tipo: 'rival', ...libres.splice(i, 1)[0] };
        }
    });

    // El resto del cuadro, sin restricciones
    for (let j = 0; j < EQUIPOS; j++) {
        if (!posiciones[j] && libres.length) posiciones[j] = { tipo: 'rival', ...libres.shift() };
    }

    // Sin filtrar los nulos: correrian los indices y miPos dejaria de apuntar a tu posicion.
    // cruzar() ya contempla un hueco como pase libre.
    return { participantes: posiciones, miPos };
}

/** Un cruce del cuadro. `resultado` queda null hasta que se juega. */
const cruce = (a, b) => ({ a, b, ganador: null, resultado: null });

/** Los cruces de una ronda, tomando de a dos a los que siguen vivos. */
export function cruzar(equipos) {
    const cruces = [];
    for (let i = 0; i < equipos.length; i += 2) cruces.push(cruce(equipos[i], equipos[i + 1] || null));
    return cruces;
}

/** Para el simulador: convierte un participante del cuadro en lo que espera simularPartido. */
const paraSimular = (e) => ({ nombreCorto: e.nombre || e.club, fuerza: e.fuerza, plantel: e.lineas });

/**
 * Resuelve un cruce que NO es el tuyo. Se juega igual —el cuadro tiene que avanzar solo— pero sin
 * transmisión: solo interesa quién pasa y con qué resultado.
 */
export function resolverCruce(c) {
    if (!c.b) return { ...c, ganador: c.a, resultado: null };   // pasa sin jugar
    const r = simularPartido(paraSimular(c.a), paraSimular(c.b));
    return {
        ...c,
        ganador: r.ganaLocal ? c.a : c.b,
        resultado: { marcador: r.marcador, penales: r.penales ? r.penales.marcador : null },
    };
}

/** ¿En qué cruce de esta ronda estás vos? */
export const indiceDeMiCruce = (cruces) =>
    cruces.findIndex(c => c.a?.tipo === 'propio' || c.b?.tipo === 'propio');

/** El rival que te toca en ese cruce. */
export function miRivalEn(cruces) {
    const i = indiceDeMiCruce(cruces);
    if (i < 0) return null;
    const c = cruces[i];
    return c.a?.tipo === 'propio' ? c.b : c.a;
}
